/**
 * webauthn-errors/keypair-binding-messages.ts — one-account-per-passkey codes
 *
 * Identifier account limits, passkey limb (ledger #90, migration 0137). Split
 * out of messages.ts for the 300-line modularity rule, mirroring the
 * master-messages.ts precedent, and re-exported into SERVER_ERROR_MESSAGES so
 * `resolveWebAuthnError` needs no separate lookup.
 *
 * Both descriptions come from `shared/webauthn-keypair-binding.ts` — the SAME
 * constants the server throws. One copy, because the no-enumeration-oracle
 * property (name no account, no username, no owner) only holds if there is a
 * single string to police; two hand-kept copies drift.
 *
 * Error philosophy (messages.ts, verbatim): never say "error" without telling
 * the user what to do; amber for user-correctable states (never red — red is
 * Forensic Pause / critical OSINT only); no presence claim anywhere — a
 * passkey tap is possession + freshness and nothing more.
 */

import {
  CREDENTIAL_ALREADY_BOUND_MESSAGE,
  CREDENTIAL_KEY_UNSUPPORTED_MESSAGE,
} from "@shared/webauthn-keypair-binding";

import type { WebAuthnUserMessage } from "./types";

export const KEYPAIR_BINDING_ERROR_MESSAGES: Record<string, WebAuthnUserMessage> = {
  /**
   * The keypair is already live on an account. Amber, not red: the user is
   * holding a working key on the wrong flow — correctable by signing in with
   * it, or reaching for another authenticator — not a security incident.
   */
  CREDENTIAL_ALREADY_BOUND: {
    title:       "This passkey is already in use",
    description: CREDENTIAL_ALREADY_BOUND_MESSAGE,
    retryable:   false,
    severity:    "amber",
  },

  /**
   * Fail-closed companion: a key whose COSE parameters cannot be canonicalised
   * is a key the one-account-per-passkey rule cannot bound, so registration is
   * refused rather than admitting an unconstrained credential.
   */
  CREDENTIAL_KEY_UNSUPPORTED: {
    title:       "This authenticator isn't supported",
    description: CREDENTIAL_KEY_UNSUPPORTED_MESSAGE,
    retryable:   false,
    severity:    "amber",
  },
};
